import { useState, useRef } from 'react';
import { Modal, Text, Button, Group, Stack } from '@mantine/core';
import { IconX, IconUpload, IconFileZip } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';

interface ImportDialogProps {
  opened: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
}

export const ImportDialog = ({ opened, onClose, onImport }: ImportDialogProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const openRef = useRef<() => void>(null);

  const handleFileDrop = (files: FileWithPath[]) => {
    if (files.length > 0) {
      setFile(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleConfirm = async () => {
    if (!file) return;

    setLoading(true);
    try {
      await onImport(file);
      setFile(null);
      onClose();
    } catch (error) {
      console.error('Import capability package failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group gap="sm">
          <IconUpload size={24} />
          <Text size="lg" fw={600}>
            {t('capability.import.title')}
          </Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="lg">
        {!file ? (
          <Dropzone
            openRef={openRef}
            onDrop={handleFileDrop}
            maxSize={50 * 1024 * 1024} // 50MB
            maxFiles={1}
            accept={[MIME_TYPES.zip]}
            styles={{
              root: {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                },
              },
            }}
          >
            <div className="py-12">
              <div className="flex justify-center mb-4">
                <IconUpload size={48} className="text-gray-400" />
              </div>
              <Text size="md" className="text-center text-gray-700 mb-2">
                {t('capability.import.uploadTitle')}
              </Text>
              <Text size="sm" className="text-center text-gray-500">
                {t('capability.import.uploadDescription')}
              </Text>
            </div>
          </Dropzone>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 rounded flex items-center justify-center">
                  <IconFileZip size={24} className="text-blue-600" />
                </div>
                <div>
                  <Text size="sm" fw={500} className="text-gray-900">
                    {file.name}
                  </Text>
                  <Text size="xs" className="text-gray-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </Text>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors"
                disabled={loading}
              >
                <IconX size={18} className="text-gray-600" />
              </button>
            </div>
          </div>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleCancel} disabled={loading}>
            {t('capability.import.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!file} loading={loading}>
            {t('capability.import.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
